/**
 * Documents per day (or week): loaded vs failed, as stacked columns.
 *
 * Follows the dataviz method: one axis, thin columns (≤ 24px) with a 4px
 * rounded data end and a 2px surface gap between the stacked segments, a
 * recessive grid, a legend (two series), status colours that never carry
 * meaning alone (legend + tooltip text), and a per-column hover tooltip with a
 * hit target the full height of the band. A table view sits behind a toggle
 * for anyone who cannot read the chart.
 */

import { useMemo, useState } from "react";
import { Box, Button, Group, Paper, Table, Text } from "@mantine/core";
import type { TrendBucketOut } from "@/shared/api/solutionPages";
import { formatCount } from "./resultsMeta";

/** Status palette (dataviz reference): fixed, never themed. */
const GOOD = "#0ca30c";
const CRITICAL = "#d03b3b";

const HEIGHT = 160;
const PAD_TOP = 12;
const PAD_BOTTOM = 22;
const PAD_LEFT = 36;
const GAP = 2;

function niceMax(v: number): number {
  if (v <= 4) return 4;
  const mag = 10 ** Math.floor(Math.log10(v));
  const step = [1, 2, 2.5, 5, 10].map((s) => s * mag).find((s) => v <= s * 4) ?? 10 * mag;
  return step * 4;
}

function label(start: string, bucket: string): string {
  const d = new Date(`${start}T00:00:00Z`);
  const text = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return bucket === "week" ? `Week of ${text}` : text;
}

interface Props {
  trend: TrendBucketOut[];
  bucket: string;
}

export function TrendChart({ trend, bucket }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const [width, setWidth] = useState(640);
  const max = useMemo(
    () => niceMax(Math.max(0, ...trend.map((b) => b.uploaded + b.failed))),
    [trend],
  );

  const plotW = Math.max(1, width - PAD_LEFT);
  const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const band = plotW / Math.max(1, trend.length);
  const barW = Math.max(2, Math.min(24, band * 0.6));
  const y = (v: number) => PAD_TOP + plotH - (v / max) * plotH;
  const ticks = [0, max / 2, max];
  const labelEvery = Math.max(1, Math.ceil(trend.length / 8));
  const hovered = hover !== null ? trend[hover] : null;

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" mb="xs">
        <div>
          <Text fw={600}>Documents per {bucket === "week" ? "week" : "day"}</Text>
          <Group gap="md" mt={4}>
            <Group gap={6}>
              <Box w={10} h={10} style={{ background: GOOD, borderRadius: 2 }} />
              <Text size="xs" c="dimmed">
                Loaded to Veeva
              </Text>
            </Group>
            <Group gap={6}>
              <Box w={10} h={10} style={{ background: CRITICAL, borderRadius: 2 }} />
              <Text size="xs" c="dimmed">
                Failed
              </Text>
            </Group>
          </Group>
        </div>
        <Button variant="subtle" size="compact-xs" onClick={() => setAsTable((v) => !v)}>
          {asTable ? "Show chart" : "Show as table"}
        </Button>
      </Group>

      {asTable ? (
        <Table striped withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{bucket === "week" ? "Week" : "Day"}</Table.Th>
              <Table.Th ta="right">Runs</Table.Th>
              <Table.Th ta="right">Loaded</Table.Th>
              <Table.Th ta="right">Failed</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {trend.map((b) => (
              <Table.Tr key={b.start}>
                <Table.Td>{label(b.start, bucket)}</Table.Td>
                <Table.Td ta="right">{formatCount(b.runs)}</Table.Td>
                <Table.Td ta="right">{formatCount(b.uploaded)}</Table.Td>
                <Table.Td ta="right">{formatCount(b.failed)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <Box
          pos="relative"
          ref={(el: HTMLDivElement | null) => {
            if (el && Math.abs(el.clientWidth - width) > 1 && el.clientWidth > 0) {
              setWidth(el.clientWidth);
            }
          }}
        >
          <svg
            width="100%"
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
            role="img"
            aria-label={`Documents loaded and failed per ${bucket}`}
            onMouseLeave={() => setHover(null)}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={PAD_LEFT}
                  x2={width}
                  y1={y(t)}
                  y2={y(t)}
                  stroke="var(--mantine-color-default-border)"
                  strokeWidth={1}
                />
                <text
                  x={PAD_LEFT - 6}
                  y={y(t) + 4}
                  textAnchor="end"
                  fontSize={10}
                  fill="var(--mantine-color-dimmed)"
                >
                  {formatCount(t)}
                </text>
              </g>
            ))}
            {trend.map((b, i) => {
              const x = PAD_LEFT + i * band + (band - barW) / 2;
              const okTop = y(b.uploaded);
              const failTop = y(b.uploaded + b.failed);
              const okH = y(0) - okTop;
              const failH = okTop - failTop - (b.uploaded > 0 && b.failed > 0 ? GAP : 0);
              const rx = Math.min(4, barW / 2);
              return (
                <g key={b.start}>
                  {b.uploaded > 0 && (
                    <path
                      d={columnPath(x, okTop, barW, okH, b.failed > 0 ? 0 : rx)}
                      fill={GOOD}
                      opacity={hover === null || hover === i ? 1 : 0.5}
                    />
                  )}
                  {b.failed > 0 && (
                    <path
                      d={columnPath(x, failTop, barW, Math.max(1, failH), rx)}
                      fill={CRITICAL}
                      opacity={hover === null || hover === i ? 1 : 0.5}
                    />
                  )}
                  {i % labelEvery === 0 && (
                    <text
                      x={x + barW / 2}
                      y={HEIGHT - 6}
                      textAnchor="middle"
                      fontSize={10}
                      fill="var(--mantine-color-dimmed)"
                    >
                      {label(b.start, "day")}
                    </text>
                  )}
                  <rect
                    x={PAD_LEFT + i * band}
                    y={PAD_TOP}
                    width={band}
                    height={plotH}
                    fill="transparent"
                    onMouseEnter={() => setHover(i)}
                    data-testid="trend-hit"
                  />
                </g>
              );
            })}
          </svg>
          {hovered && hover !== null && (
            <Paper
              shadow="sm"
              withBorder
              p={6}
              radius="sm"
              style={{
                position: "absolute",
                top: 0,
                left: Math.min(width - 170, Math.max(0, PAD_LEFT + hover * band + band / 2 - 80)),
                pointerEvents: "none",
                width: 160,
              }}
            >
              <Text size="xs" fw={600}>
                {label(hovered.start, bucket)}
              </Text>
              <Text size="xs">Runs: {formatCount(hovered.runs)}</Text>
              <Text size="xs">Loaded: {formatCount(hovered.uploaded)}</Text>
              <Text size="xs">Failed: {formatCount(hovered.failed)}</Text>
            </Paper>
          )}
        </Box>
      )}
    </Paper>
  );
}

/** A column with rounded top corners only (square at the baseline / the joint). */
function columnPath(x: number, top: number, w: number, h: number, r: number): string {
  if (h <= 0) return "";
  const rr = Math.min(r, h);
  return [
    `M${x},${top + h}`,
    `V${top + rr}`,
    `Q${x},${top} ${x + rr},${top}`,
    `H${x + w - rr}`,
    `Q${x + w},${top} ${x + w},${top + rr}`,
    `V${top + h}`,
    "Z",
  ].join(" ");
}
